import React, {ReactNode} from 'react'
import {FormattedDate} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {Button} from '@vio/atlas/components/designSystem/Button'
import {Divider} from '@vio/atlas/components/designSystem/Divider'
import {PopUp} from '@vio/atlas/components/designSystem/PopUp'
import {Text} from '@vio/atlas/components/designSystem/Text'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getDatePart} from '@vio/core/src/utils/date'

interface Props {
  title: ReactNode
  message?: ReactNode
  isOpen: boolean
  closeModal: () => void
  transitionDate: string
  checkInDate: string
  checkOutDate: string
}

interface InstructionRowProps extends Pick<Props, 'transitionDate'> {
  title: JSX.Element
  message?: JSX.Element
  isHighlighted?: boolean
  dataId?: string
}

interface DateBoxProps extends Pick<InstructionRowProps, 'isHighlighted'> {
  date: string
  dataId?: string
}

interface ContentProps
  extends Pick<
    Props,
    'transitionDate' | 'checkInDate' | 'checkOutDate' | 'message'
  > {}

const DateBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 44px;
  height: 44px;
  border-radius: ${cssTheme.layout.radius.lg};
  background-color: ${cssTheme.colors.background.neutral.secondary};
  justify-content: center;
  align-items: center !important;
  padding: ${cssTheme.layout.spacing.s200} ${cssTheme.layout.spacing.s400};
  white-space: nowrap;
  flex: 1;
  &.--is-highlighted {
    background-color: ${cssTheme.colors.background.warning.secondary};
  }
`

const Wrapper = styled.div`
  max-width: 470px;
  word-wrap: break-word;
  width: 100%;
`

const DateBox = ({date, isHighlighted, dataId}: DateBoxProps) => {
  return (
    <DateBoxWrapper className={cx(isHighlighted && '--is-highlighted')}>
      <Text
        variant="bodyXS"
        colorPath={
          isHighlighted
            ? 'content.warning.primary'
            : 'content.neutral.secondary'
        }
        dataId={`splitBooking.month.${dataId}`}
      >
        <FormattedDate value={date} month="short" timeZone="UTC" />
      </Text>
      <Text
        variant="labelS"
        colorPath={
          isHighlighted ? 'content.warning.primary' : 'content.neutral.primary'
        }
        dataId={`splitBooking.date.${dataId}`}
      >
        <FormattedDate value={date} day="2-digit" timeZone="UTC" />
      </Text>
    </DateBoxWrapper>
  )
}

const InstructionRow = ({
  transitionDate,
  title,
  message,
  isHighlighted,
  dataId
}: InstructionRowProps) => {
  return (
    <Grid container gap="s500" spacing="s000" direction="row" wrap="nowrap">
      <DateBox
        date={transitionDate}
        isHighlighted={isHighlighted}
        dataId={dataId}
      />

      <Grid container spacing="s000" justify="center" direction="column">
        <Text variant="titleM">{title}</Text>
        <Text variant="bodyS" colorPath="content.neutral.secondary">
          {message}
        </Text>
      </Grid>
    </Grid>
  )
}

const Content = ({
  message,
  transitionDate,
  checkInDate,
  checkOutDate
}: ContentProps) => {
  return (
    <Wrapper data-id="split-booking-instruction-modal">
      {message && (
        <Box paddingBottom="s500">
          <Text variant="bodyM">{message}</Text>
        </Box>
      )}
      <Box paddingTop="s500" paddingBottom="s500">
        <InstructionRow
          title={
            <FormattedMessageWrapper id="checkIn" defaultMessage="Check-in" />
          }
          message={
            <FormattedMessageWrapper
              id="splitBooking.checkInMessage"
              defaultMessage="Remind the front desk that you have two bookings."
            />
          }
          transitionDate={checkInDate}
          dataId="checkIn"
        />
      </Box>
      <Divider />
      <Box paddingTop="s500" paddingBottom="s500">
        <InstructionRow
          title={
            <FormattedMessageWrapper
              id="splitBooking.switchRoom"
              defaultMessage="Switch room"
            />
          }
          message={
            <FormattedMessageWrapper
              id="splitBooking.transitionDateMessage"
              defaultMessage="If required by the property, you’ll switch rooms on {transitionDate}"
              values={{
                transitionDate: (
                  <FormattedDate
                    value={getDatePart(transitionDate)}
                    day="2-digit"
                    month="short"
                    timeZone="UTC"
                  />
                )
              }}
            />
          }
          transitionDate={transitionDate}
          isHighlighted
          dataId="transitionTime"
        />
      </Box>
      <Divider />
      <Box paddingTop="s500" paddingBottom="s500">
        <InstructionRow
          title={
            <FormattedMessageWrapper id="checkOut" defaultMessage="Check-out" />
          }
          transitionDate={checkOutDate}
          dataId="checkOut"
        />
      </Box>
    </Wrapper>
  )
}

export const SplitBookingInstructionModal = ({
  isOpen,
  closeModal,
  title,
  message,
  transitionDate,
  checkInDate,
  checkOutDate
}: Props) => {
  const {isMobile} = useDeviceLayout()

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        navBarProps={{
          centerContent: <Text variant="titleM">{title}</Text>
        }}
        onClose={closeModal}
        footerContent={
          <Box paddingBottom="s600" paddingRight="s500" paddingLeft="s500">
            <Button variant="tertiary" size="lg" onClick={closeModal} fullWidth>
              <Text variant="labelM">
                <FormattedMessageWrapper
                  id="gotItButton"
                  defaultMessage="Ok, got it"
                />
              </Text>
            </Button>
          </Box>
        }
      >
        <Box paddingTop="s300">
          <Content
            message={message}
            transitionDate={transitionDate}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
          />
        </Box>
      </BottomSheet>
    )
  }
  return (
    <PopUp
      isOpen={isOpen}
      closeAction={closeModal}
      navBarProps={{
        centerContent: <Text variant="titleL">{title}</Text>
      }}
      width={520}
      footer={
        <Button variant="tertiary" size="lg" onClick={closeModal} fullWidth>
          <Text variant="labelM">
            <FormattedMessageWrapper
              id="gotItButton"
              defaultMessage="Ok, got it"
            />
          </Text>
        </Button>
      }
    >
      <Content
        message={message}
        transitionDate={transitionDate}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
      />
    </PopUp>
  )
}
